// src/pages/orders/OrderList.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import API_BASE_URL from "../../utils/config";

function currentFYRange() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth(); // 0=Jan
  const startYear = m >= 3 ? y : y - 1;
  const start = new Date(startYear, 3, 1); // Apr 1
  const end = new Date(startYear + 1, 2, 31); // Mar 31 (next)
  const toYMD = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
  return { from: toYMD(start), to: toYMD(end) };
}

const moneyINR = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleDateString("en-GB");
  } catch {
    return "-";
  }
};

export default function OrderList() {
  const token = localStorage.getItem("token");
  const fy = useMemo(currentFYRange, []);

  // Filters
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all"); // all | pending | invoiced
  const [from, setFrom] = useState(fy.from);
  const [to, setTo] = useState(fy.to);

  // Data
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // (Optional) client totals
  const totals = useMemo(
    () => ({
      count: orders.length,
      amount: orders.reduce((s, o) => s + Number(o.total_amount || 0), 0),
    }),
    [orders]
  );

  const fetchOrders = async (signal) => {
    try {
      setLoading(true);
      setErr("");
      const res = await axios.get(`${API_BASE_URL}/api/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        // If your backend supports these, it will filter; if not, it’ll just ignore them.
        params: {
          q: search || undefined,
          status: status === "all" ? undefined : status, // pending/invoiced
          from,
          to,
        },
        signal,
      });
      setOrders(Array.isArray(res.data) ? res.data : res.data?.rows || []);
    } catch (e) {
      if (axios.isCancel && axios.isCancel(e)) return;
      console.error("Failed to load orders:", e);
      setErr("Failed to load orders.");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const c = new AbortController();
    fetchOrders(c.signal);
    return () => c.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, from, to]);

  const resetFY = () => {
    setFrom(fy.from);
    setTo(fy.to);
  };

  const generateInvoice = async (orderId) => {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/invoices`,
        { order_id: orderId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Invoice generated successfully");
      // refresh list
      fetchOrders();
    } catch (err) {
      console.error("Invoice generation failed", err);
      alert("Failed to generate invoice");
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Order List</h2>
          <div className="text-xs text-slate-500">
            Range: {from} → {to}
          </div>
        </div>
        <Link
          to="/orders/new"
          className="inline-flex items-center justify-center rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700"
        >
          Add Order
        </Link>
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl shadow p-3 flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1">
          <label className="text-xs text-slate-500 mb-1 block">Search</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by customer / doctor / order #"
            className="w-full border rounded px-3 py-2"
          />
        </div>

        <div>
          <label className="text-xs text-slate-500 mb-1 block">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border rounded px-3 py-2 w-full"
          >
            <option value="all">All</option>
            <option value="pending">Pending (no invoice)</option>
            <option value="invoiced">Invoiced</option>
          </select>
        </div>

        <div>
          <label className="text-xs text-slate-500 mb-1 block">From</label>
          <input
            type="date"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
            className="border rounded px-3 py-2"
          />
        </div>

        <div>
          <label className="text-xs text-slate-500 mb-1 block">To</label>
          <input
            type="date"
            value={to}
            min={from}
            onChange={(e) => setTo(e.target.value)}
            className="border rounded px-3 py-2"
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => fetchOrders()}
            className="px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            Apply
          </button>
          <button
            onClick={resetFY}
            title={`Reset to FY ${fy.from} → ${fy.to}`}
            className="px-3 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            Current FY
          </button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-2xl overflow-hidden border bg-white">
          <div className="h-1 bg-gradient-to-r from-indigo-500 to-blue-500" />
          <div className="p-3">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">
              Orders
            </div>
            <div className="text-lg font-semibold">{totals.count}</div>
          </div>
        </div>
        <div className="rounded-2xl overflow-hidden border bg-white">
          <div className="h-1 bg-gradient-to-r from-emerald-500 to-teal-500" />
          <div className="p-3">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">
              Total Amount
            </div>
            <div className="text-lg font-semibold">{moneyINR(totals.amount)}</div>
          </div>
        </div>
      </div>

      {err && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {err}
        </div>
      )}

      {/* ===== MOBILE: cards ===== */}
      <div className="md:hidden space-y-3">
        {loading && (
          <div className="rounded-xl border bg-white p-4 text-center text-slate-500">
            Loading…
          </div>
        )}

        {!loading && orders.length === 0 && (
          <div className="rounded-xl border bg-white p-4 text-center text-slate-500">
            No orders.
          </div>
        )}

        {!loading &&
          orders.map((order) => (
            <div
              key={order.id}
              className="rounded-2xl border bg-white p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-base font-semibold text-slate-900">
                    Order #{order.id} by {order.sale_by_name || "—"}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                      {order.customer_name || "—"}
                    </span>
                    {order.doctor_name && (
                      <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                        {order.doctor_name}
                      </span>
                    )}
                    <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                      {fmtDate(order.order_date)}
                    </span>
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-sm font-semibold text-emerald-700">
                    {moneyINR(order.total_amount)}
                  </div>
                  <div className="text-xs mt-1">
                    {order.invoice_id ? (
                      <span className="inline-flex items-center rounded bg-blue-50 px-2 py-0.5 text-blue-700">
                        Invoiced
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded bg-amber-50 px-2 py-0.5 text-amber-700">
                        Pending
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-3">
                {order.invoice_id ? (
                  <Link
                    to={`/invoices/${order.invoice_id}`}
                    className="inline-flex w-full items-center justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
                  >
                    View Invoice
                  </Link>
                ) : (
                 <Link to={`/invoices/add?orderId=${order.id}`} className="bg-green-700 text-white px-2 py-1 rounded">
  Create Invoice
</Link>
                )}
              </div>
            </div>
          ))}
      </div>

      {/* ===== DESKTOP/TABLET: table ===== */}
      <div className="hidden md:block overflow-x-auto rounded-2xl border bg-white shadow-sm">
        <table className="w-full text-sm text-left">
          <thead className="bg-slate-50 text-slate-700">
            <tr>
              <th className="px-4 py-3 font-medium">Order ID</th>
              <th className="px-4 py-3 font-medium">Customer</th>
              <th className="px-4 py-3 font-medium">Doctor</th>
              <th className="px-4 py-3 font-medium text-right">Amount</th>
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Order By</th>

              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            )}

            {!loading &&
              orders.map((order) => (
                <tr key={order.id} className="border-t hover:bg-slate-50 transition">
                  <td className="px-4 py-3">#{order.id}</td>
                  <td className="px-4 py-3">{order.customer_name}</td>
                  <td className="px-4 py-3">{order.doctor_name || "—"}</td>
                  <td className="px-4 py-3 text-right text-emerald-700 font-semibold">
                    {moneyINR(order.total_amount)}
                  </td>
                  <td className="px-4 py-3">{fmtDate(order.order_date)}</td>
                  <td className="px-4 py-3">
                    {order.invoice_id ? (
                      <span className="inline-flex items-center rounded bg-blue-50 px-2 py-0.5 text-blue-700">
                        Invoiced
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded bg-amber-50 px-2 py-0.5 text-amber-700">
                        Pending
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">{order.sale_by_name || "—"}</td>

                  <td className="px-4 py-3">
                    {order.invoice_id ? (
                      <Link
                        to={`/invoices/${order.invoice_id}`}
                        className="text-blue-600 hover:underline"
                      >
                        View Invoice
                      </Link>
                    ) : (
                     <Link to={`/invoices/add?orderId=${order.id}`} className="bg-green-700 text-white px-2 py-1 rounded">
  Create Invoice
</Link>
                    )}
                  </td>
                </tr>
              ))}

            {!loading && orders.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-slate-500 py-6">
                  No orders.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
